import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import * as BenchlingUtil from 'BenchlingUtil';
import {ActionMethod, ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {buildComputedTable} from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import getArchivedRows from 'notebook/structured-tables/util/getArchivedRows';
import {getEvaluatedTableErrors} from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {hasErrorType, isRequiredFieldError, LoadingError} from 'notebook/tables/excess/Util';
import {TableColumnType} from 'notebook/tables/TableConstants';
import {CreateOrUpdateServerProcessTask, CreateTaskForNewProcess} from 'processes/dao/ProcessManagementDAO';
import {EVALUATED_TABLE_ERROR_OPTIONS} from 'processes/ProcessConstants';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import taskTableToTasks from 'processes/tables/taskTableToTasks';
import {BenchlingState} from 'redux-stores/reduceApp';

interface SeedTasksResult {
  seedTasks: Array<CreateOrUpdateServerProcessTask | CreateTaskForNewProcess>;
  isLoading: boolean;
  footerMessage: {header: string; supportingInfo?: string} | undefined;
  dismissFooterMessage: () => void;
}

export default function useSeedTasks(
  taskTableWithPropagatedContent: ProcessTaskStructuredTable | undefined
): SeedTasksResult {
  const [seedTasks, setSeedTasks] = useState<
    Array<CreateOrUpdateServerProcessTask | CreateTaskForNewProcess>
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [footerMessage, setFooterMessage] = useState<{header: string; supportingInfo?: string} | undefined>(
    undefined
  );

  const stringResolutionLoadingState = useSelector(
    (state: BenchlingState) => state.models.eln.stringResolutionLoadingState
  );

  useEffect(() => {
    if (taskTableWithPropagatedContent) {
      const taskTable = taskTableWithPropagatedContent;
      const computedTable = buildComputedTable(taskTableWithPropagatedContent, null);
      const doesComputedTableHaveLoadingCells = computedTable.some((row) =>
        row.some((cell) => (cell.error ? hasErrorType(cell.error, LoadingError.type) : false))
      );
      const isStringResolutionLoading = Object.values(stringResolutionLoadingState).some(
        (loadingState) => loadingState
      );
      const isLoading = doesComputedTableHaveLoadingCells || isStringResolutionLoading;
      setIsLoading(isLoading);

      if (isLoading) {
        return;
      }

      const requestTableErrors = getEvaluatedTableErrors(
        computedTable,
        {
          ...EVALUATED_TABLE_ERROR_OPTIONS,
          archivedRows: taskTable.columns.some((col) => col.columnType === TableColumnType.PROCESS_TASK)
            ? getArchivedRows(taskTable, computedTable)
            : [],
        },
        false
      );

      const onlyRequiredFieldErrors = requestTableErrors.every((maybeErrorArray) =>
        Array.isArray(maybeErrorArray)
          ? maybeErrorArray.every((e) => isRequiredFieldError(e))
          : isRequiredFieldError(maybeErrorArray)
      );

      if (requestTableErrors.length > 0 && !onlyRequiredFieldErrors) {
        setFooterMessage({
          header: `Some fields in the request may not be autofilled based on the selected ${BenchlingUtil.pluralize(taskTable.rows.length, 'item')}.`,
          supportingInfo:
            "This can happen if the data is incomplete or doesn't match the request format. You can still proceed by creating this request and entering the information manually.",
        });
      }

      setSeedTasks(
        taskTableToTasks({
          table: taskTable,
          evaluatedTable: computedTable,
          sourceType: `structured_table_request_v2_shortcut`,
          actionOrigin: ActionOrigin.NOTEBOOK_UNDETERMINED_STRUCTURED_TABLE,
          actionMethod: ActionMethod.BUTTON_CLICK,
          ignoreErrors: onlyRequiredFieldErrors,
        })
      );
    }
  }, [taskTableWithPropagatedContent, stringResolutionLoadingState]);

  return {
    seedTasks,
    isLoading,
    footerMessage,
    dismissFooterMessage: () => {
      setFooterMessage(undefined);
    },
  };
}
